import React, { Fragment, useEffect, useState } from 'react';

import { postResources2 } from '@api/FW/balance/balance.api';
import { apiErrorHandler } from '@app/services/error.service';
import { NOMENCLATURE_UAH } from '@app/shared/constants/globalConstans';
import { FONT_SIZE, FONT_WEIGHT } from '@app/style/constants';
import { useAppSelector } from '@hooks/shared/reduxHooks';
import { Container } from '@styled/shared/Container';
import { formatNumberWithDecimals } from '@utils/shared/number/formatNumberWithDecimals';
import { formatReadableNumber } from '@utils/shared/number/formatReadableNumber';
import { Divider, Flex, Skeleton, Typography } from 'antd';
import { t } from 'i18next';

const { Text } = Typography;

interface IUserData {
	title: string;
	value: string | number;
	isValueLoading: boolean;
	copyable?: boolean;
}

const UserData = () => {
	const wallets = useAppSelector((state) => state.wallets.data);
	const client = useAppSelector((state) => state.user.client);

	const initial = [
		'aboutCompany.counterpartyName',
		'aboutCompany.TIN',
		'aboutCompany.statusText',
		'aboutCompany.leftover',
	].map((item) => ({ title: item, value: '', isValueLoading: true }));

	const [userData, setUserData] = useState<IUserData[]>(initial);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		if (!client) return;
		setIsLoading(true);
		const data: IUserData[] = [
			{
				title: 'aboutCompany.counterpartyName',
				value: client.nameFull,
				isValueLoading: false,
			},
			{
				title: 'aboutCompany.TIN',
				value: client.codeEgrpou,
				isValueLoading: false,
				copyable: true,
			},
			{
				title: 'aboutCompany.statusText',
				value: client.blocked ? 'aboutCompany.inactive' : 'aboutCompany.active',
				isValueLoading: false,
			},
			{
				title: 'aboutCompany.leftover',
				value: '',
				isValueLoading: true,
			},
		];

		setUserData(data);
		setIsLoading(false);
	}, [client]);

	/**
	 * Get wallets balances.
	 */
	useEffect(() => {
		if (!wallets.length || isLoading) return;

		postResources2(
			'wallet',
			wallets.map((item) => item.walletGUID),
		)
			.then((loadedData) => {
				let balanceSum = 0;

				loadedData.forEach(({ nomenclature, balance }) => {
					if (nomenclature === NOMENCLATURE_UAH) balanceSum = balanceSum + balance;
				});

				const updatedUserData = userData.map((item) =>
					item.title === 'aboutCompany.leftover'
						? {
								...item,
								value: (Math.round(balanceSum * 100) / 100)?.toString(),
								isValueLoading: false,
							}
						: item,
				);

				setUserData(updatedUserData);
			})
			.catch((error) => {
				apiErrorHandler(error);
			});
	}, [wallets, isLoading]);

	return (
		<Container vertical className='content-wrapper' gap={8} wrap='wrap'>
			{userData.map(({ title, value, isValueLoading, copyable }, index) => (
				<Fragment key={title}>
					<Flex gap={8} justify='space-between' className='user-data__unit'>
						<Text type='secondary' style={{ minWidth: '75px' }}>
							{t(title)}
						</Text>
						{isValueLoading ? (
							<Skeleton.Button active shape='round' size='small' />
						) : (
							<Text
								copyable={copyable}
								style={{ fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.medium }}>
								{title === 'aboutCompany.leftover'
									? formatReadableNumber(Number(formatNumberWithDecimals(Number(value))))
									: t(value?.toString())}
							</Text>
						)}
					</Flex>
					{index !== userData.length - 1 && <Divider style={{ margin: '0' }} />}
				</Fragment>
			))}
		</Container>
	);
};

export default UserData;
